import { router } from "@inertiajs/react"
import { Check, Copy, RefreshCw, Shield } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field } from "@/components/steel"
import { Input } from "@/components/ui/input"

const ALPHABET = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789"

export function generatePassword(length = 14): string {
  const bytes = new Uint32Array(length)
  crypto.getRandomValues(bytes)
  return [...bytes].map((b) => ALPHABET[b % ALPHABET.length]).join("")
}

export function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false)

  return (
    <Button
      variant="secondary"
      size="xs"
      onClick={() => {
        void navigator.clipboard.writeText(value)
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
      }}
    >
      {copied ? <Check /> : <Copy />}
      {copied ? "Copied" : label}
    </Button>
  )
}

export type DialogMode =
  | { kind: "add" }
  | { kind: "reset"; user: { id: number; name: string; email: string } }

export function UserPasswordDialog({
  mode,
  onClose,
  suggestedPassword,
}: {
  mode: DialogMode
  onClose: () => void
  suggestedPassword: string
}) {
  const isReset = mode.kind === "reset"
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState(suggestedPassword)

  const ready = password.length >= 6 && (isReset || (name.trim().length > 0 && email.trim().length > 0))

  const submit = () => {
    if (!ready) return
    if (mode.kind === "reset") {
      router.post(`/users/${mode.user.id}/reset_password`, { password })
    } else {
      router.post("/users", { email, name, password })
    }
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isReset ? "Reset password" : "Add a user"}</DialogTitle>
          <DialogDescription>
            {isReset
              ? `Give ${mode.user.name} a new temporary password. They will be asked to replace it when they next sign in.`
              : "Set a temporary password and pass it on. They will be asked to replace it on first sign-in."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {isReset ? null : (
            <>
              <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan Rivera" />
              </Field>
              <Field label="Work email">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jordan@realsteelsolutions.com"
                />
              </Field>
            </>
          )}

          <Field label="Temporary password">
            <div className="flex items-center gap-2">
              <Input className="num flex-1" value={password} onChange={(e) => setPassword(e.target.value)} />
              <Button
                variant="outline"
                size="icon-sm"
                title="Generate another"
                onClick={() => setPassword(generatePassword())}
              >
                <RefreshCw />
              </Button>
              <CopyButton value={password} />
            </div>
          </Field>
          <p className="-mt-1 text-meta text-muted-foreground">
            At least 6 characters. Nothing is emailed — share this yourself.
          </p>

          {isReset ? null : (
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <span className="flex items-center gap-2 text-body">
                <Shield className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                Access
              </span>
              <Badge variant="secondary">Admin — full access</Badge>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={!ready}>
            {isReset ? "Reset password" : "Add user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function CredentialsDialog({
  credentials,
  onAddAnother,
  onClose,
}: {
  credentials: { email: string; password: string; name: string }
  onAddAnother: () => void
  onClose: () => void
}) {
  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share these with {credentials.name}</DialogTitle>
          <DialogDescription>
            This password is shown once. They will be asked to replace it when they sign in.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <CredentialRow label="Email" value={credentials.email} />
          <CredentialRow label="Temporary password" value={credentials.password} mono />
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onAddAnother}>
            Add another
          </Button>
          <Button size="sm" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CredentialRow({ label, mono, value }: { label: string; mono?: boolean; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-meta text-muted-foreground">{label}</span>
      <div className="flex h-[34px] items-center gap-2 rounded-md border border-input pl-2.5 pr-1">
        <span className={`min-w-0 flex-1 truncate text-label ${mono ? "num" : ""}`}>{value}</span>
        <CopyButton value={value} />
      </div>
    </div>
  )
}
